import { useState } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { useCurrentUser } from '../../hooks/useCurrentUser';
import { useAdmin } from '../../hooks/useAdmin';
import { useAnalyticsAccess } from '../../hooks/useAnalyticsAccess';

export default function UserDebugPanel() {
  const [isOpen, setIsOpen] = useState(false);
  const { user: authUser, token, isAuthenticated } = useAuth();
  const { data: currentUser, isLoading, error } = useCurrentUser();
  const { isAdmin, currentUser: adminUser } = useAdmin();
  const { hasAnalyticsAccess, environmentInfo, accessReason } = useAnalyticsAccess();

  // Only show in development
  if (import.meta.env.PROD) return null;

  return (
    <div className="fixed top-4 right-4 z-50">
      {/* Toggle Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-2 rounded-lg shadow-lg text-sm font-medium mb-2"
        title="Toggle Debug Panel"
      >
        🔍 Debug User
      </button>

      {/* Debug Panel */}
      {isOpen && (
        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg shadow-xl p-4 w-96 max-h-96 overflow-y-auto">
          <div className="flex justify-between items-center mb-3">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">User Debug Panel</h3>
            <button
              onClick={() => setIsOpen(false)}
              className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
            >
              ✕
            </button>
          </div>

          <div className="space-y-4 text-sm">
            {/* Authentication Status */}
            <div className="border-b border-gray-200 dark:border-gray-600 pb-2">
              <h4 className="font-semibold text-gray-700 dark:text-gray-300 mb-1">Auth Status</h4>
              <div className="space-y-1 text-xs">
                <p><span className="font-medium">Authenticated:</span> {isAuthenticated ? '✅ Yes' : '❌ No'}</p>
                <p><span className="font-medium">Has Token:</span> {token ? '✅ Yes' : '❌ No'}</p>
                <p><span className="font-medium">Is Admin:</span> {isAdmin ? '✅ Yes' : '❌ No'}</p>
              </div>
            </div>

            {/* Auth User (from Zustand store) */}
            <div className="border-b border-gray-200 dark:border-gray-600 pb-2">
              <h4 className="font-semibold text-gray-700 dark:text-gray-300 mb-1">Auth User (Store)</h4>
              {authUser ? (
                <pre className="bg-gray-100 dark:bg-gray-700 p-2 rounded text-xs overflow-x-auto">
                  {JSON.stringify(authUser, null, 2)}
                </pre>
              ) : (
                <p className="text-gray-500 text-xs">No auth user</p>
              )}
            </div>

            {/* Current User (from API) */}
            <div className="border-b border-gray-200 dark:border-gray-600 pb-2">
              <h4 className="font-semibold text-gray-700 dark:text-gray-300 mb-1">Current User (API)</h4>
              {isLoading ? (
                <p className="text-blue-500 text-xs">Loading...</p>
              ) : error ? (
                <p className="text-red-500 text-xs">Error: {String(error)}</p>
              ) : currentUser ? (
                <pre className="bg-gray-100 dark:bg-gray-700 p-2 rounded text-xs overflow-x-auto">
                  {JSON.stringify(currentUser, null, 2)}
                </pre>
              ) : (
                <p className="text-gray-500 text-xs">No current user</p>
              )}
            </div>

            {/* Admin User (from useAdmin hook) */}
            <div className="border-b border-gray-200 dark:border-gray-600 pb-2">
              <h4 className="font-semibold text-gray-700 dark:text-gray-300 mb-1">Admin User (Hook)</h4>
              {adminUser ? (
                <pre className="bg-gray-100 dark:bg-gray-700 p-2 rounded text-xs overflow-x-auto">
                  {JSON.stringify(adminUser, null, 2)}
                </pre>
              ) : (
                <p className="text-gray-500 text-xs">No admin user</p>
              )}
            </div>

            {/* Admin Detection Logic */}
            <div>
              <h4 className="font-semibold text-gray-700 dark:text-gray-300 mb-1">Admin Detection</h4>
              <div className="space-y-1 text-xs">
                {currentUser ? (
                  <>
                    <p><span className="font-medium">userType:</span> {currentUser.userType || 'undefined'}</p>
                    <p><span className="font-medium">email:</span> {currentUser.email || 'undefined'}</p>
                    <p><span className="font-medium">isAdmin flag:</span> {(currentUser as any).isAdmin ? '✅ Yes' : '❌ No'}</p>
                    <p><span className="font-medium">Is staff + @exac.com:</span> {
                      currentUser.userType === 'staff' && currentUser.email?.includes('@exac.com') ? '✅ Yes' : '❌ No'
                    }</p>
                    <p><span className="font-medium">Is hardcoded admin:</span> {
                      currentUser.email.includes('@exac.com') ? '✅ Yes (EXAC staff)' : '❌ No'
                    }</p>
                    <p className="font-medium mt-1 p-1 bg-yellow-100 dark:bg-yellow-900 rounded">
                      Final Admin Status: {isAdmin ? '✅ ADMIN' : '❌ NOT ADMIN'}
                    </p>
                  </>
                ) : (
                  <p className="text-gray-500">No user data available</p>
                )}
              </div>
            </div>

            {/* Analytics Access Info */}
            <div>
              <h4 className="font-semibold text-gray-700 dark:text-gray-300 mb-1">Analytics Access</h4>
              <div className="space-y-1 text-xs">
                <p><span className="font-medium">Environment:</span> {environmentInfo.environment}</p>
                <p><span className="font-medium">Has Analytics Access:</span> {hasAnalyticsAccess ? '✅ Yes' : '❌ No'}</p>
                <p><span className="font-medium">Access Reason:</span> {accessReason}</p>
                <p><span className="font-medium">Public Access Enabled:</span> {environmentInfo.analyticsPublicAccess ? '✅ Yes' : '❌ No'}</p>
                <div className="mt-2 p-2 bg-blue-100 dark:bg-blue-900 rounded text-blue-800 dark:text-blue-200">
                  <p className="font-medium">Environment Rules:</p>
                  <ul className="ml-2 mt-1">
                    <li>• Development: Public access ✅</li>
                    <li>• Staging: Public access ✅</li>
                    <li>• Production: Admin only 🔒</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Token Info */}
            {token && (
              <div>
                <h4 className="font-semibold text-gray-700 dark:text-gray-300 mb-1">Token Info</h4>
                <div className="text-xs">
                  <p><span className="font-medium">Length:</span> {token.length} chars</p>
                  <p><span className="font-medium">Starts with:</span> {token.substring(0, 20)}...</p>
                  <p><span className="font-medium">Expires:</span> {
                    localStorage.getItem('chime_token_expires_at') 
                      ? new Date(parseInt(localStorage.getItem('chime_token_expires_at')!)).toLocaleString()
                      : 'Unknown'
                  }</p>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}